import { del, get, post, put } from '@/lib/api/client'
import {
  LegalFoundationFilterParamsSchema,
  PaginationParamsSchema,
  type LegalFoundation,
  type LegalFoundationCategory,
  type LegalFoundationFilterParams,
  type LegalFoundationStats,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  CreateLegalFoundation,
  CreateLegalFoundationSchema,
  UpdateLegalFoundation,
  UpdateLegalFoundationSchema
} from '@/lib/api/types'

export class LegalFoundationService {

  async getLegalFoundations(
    params: PaginationParams = { page: 1, size: 10 },
    filters: LegalFoundationFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<LegalFoundation>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = LegalFoundationFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<LegalFoundation>>>(
      `legal-foundations/private/filter?${query}`,
      validatedFilters
    )
  }


  async getLegalFoundationStats(): Promise<ApiResponse<LegalFoundationStats>> {
    const response = await get<ApiResponse<LegalFoundationStats>>('legal-foundations/private/stats')
    return response
  }


  async getLegalFoundationById(legalFoundationId: string): Promise<ApiResponse<LegalFoundation>> {
    return await get<ApiResponse<LegalFoundation>>(
      `legal-foundations/private/${legalFoundationId}`
    )
  }

  async createLegalFoundation(data: CreateLegalFoundation): Promise<ApiResponse<LegalFoundation>> {

    const validationData = CreateLegalFoundationSchema.parse(data)

    return await post<ApiResponse<LegalFoundation>>(
      'legal-foundations/admin/private',
      validationData
    )
  };

  async updateLegalFoundation(legalFoundationId: string, data: UpdateLegalFoundation): Promise<ApiResponse<LegalFoundation>> {

    const validationData = UpdateLegalFoundationSchema.parse(data)

    return await put<ApiResponse<LegalFoundation>>(
      `legal-foundations/admin/private/${legalFoundationId}`,
      validationData
    )
  }


  async updateLegalFoundationStatus(legalFoundationId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<LegalFoundation>> {
    return await put<ApiResponse<LegalFoundation>>(
      `legal-foundations/admin/private/${legalFoundationId}/status`,
      { status }
    )
  }

  async deleteLegalFoundation(legalFoundationId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `legal-foundations/admin/private/${legalFoundationId}`
    )
  }

  async getLegalFoundationCategories(): Promise<ApiResponse<LegalFoundationCategory[]>> {
    return await get<ApiResponse<LegalFoundationCategory[]>>(
      'legal-foundations/private/categories'
    )
  }

  async searchLegalFoundations(
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<LegalFoundation>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const queryParams = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    })

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<LegalFoundation>>>(
      `legal-foundations/private/search?${queryParams.toString()}`
    )
  }

  /**
   * Get Key Resources highlights
   * GET /legal-foundations/public/highlights?type=HOME_PAGE_KEY_RESOURCES
   */
  async getLegalFoundationHighlights(type: string = 'HOME_PAGE_KEY_RESOURCES'): Promise<ApiResponse<LegalFoundation[]>> {
    return await get<ApiResponse<LegalFoundation[]>>(
      `legal-foundations/public/highlights?type=${type}`
    )
  }

  /**
   * Update Key Resources highlights
   * POST /legal-foundations/private/highlight
   */
  async updateLegalFoundationHighlights(
    legalFoundationIds: string[],
    type: string = 'HOME_PAGE_KEY_RESOURCES'
  ): Promise<ApiResponse<null>> {
    return await post<ApiResponse<null>>('legal-foundations/private/highlight', {
      legalFoundationIds,
      highlightType: type
    })
  }
}


export const legalFoundationService = new LegalFoundationService()

// Export default
export default legalFoundationService
